import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Truck, Settings, Plus, Edit2, Trash2, Search,
  Gauge, Zap, Calendar, Loader, X, CheckCircle
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)', surf3: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', bord2: 'var(--nd-w10)',
  text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', amber: '#FBBF24', red: '#F87171',
  green: '#34D399', cyan: '#22D3EE', font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

  /* search */
  .vmm-input { width:100%; padding:8px 11px 8px 32px; background:var(--nd-w05); border:1px solid var(--nd-w10); border-radius:9px; color:var(--nd-text); font-size:12px; font-family:'DM Sans',sans-serif; outline:none; transition:border-color 0.15s,background 0.15s; }
  .vmm-input::placeholder { color:var(--nd-tx22); }
  .vmm-input:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }

  /* table */
  .vmm-th { padding:9px 13px; font-size:9px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--nd-tx30); font-family:'DM Sans',sans-serif; border-bottom:1px solid var(--nd-w08); white-space:nowrap; background:var(--nd-surface-2); }
  .vmm-tr { border-bottom:1px solid var(--nd-w05); transition:background 0.12s; }
  .vmm-tr:last-child { border-bottom:none; }
  .vmm-tr:hover td { background:var(--nd-w03); }
  .vmm-td { padding:10px 13px; vertical-align:middle; }

  /* action buttons */
  .vmm-act { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:7px; border:1px solid; cursor:pointer; transition:all 0.14s; }
  .vmm-act-edit   { background:rgba(59,130,246,0.09);  color:#60A5FA; border-color:rgba(59,130,246,0.22); }
  .vmm-act-edit:hover   { background:rgba(59,130,246,0.18); border-color:rgba(59,130,246,0.42); }
  .vmm-act-delete { background:rgba(248,113,113,0.09); color:#F87171; border-color:rgba(248,113,113,0.22); }
  .vmm-act-delete:hover { background:rgba(248,113,113,0.18); border-color:rgba(248,113,113,0.42); }

  /* add button */
  .vmm-btn-add { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:9px; border:none; background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:12px; font-weight:700; cursor:pointer; box-shadow:0 4px 12px rgba(59,130,246,0.22); transition:opacity 0.15s; white-space:nowrap; }
  .vmm-btn-add:hover { opacity:.88; }

  /* modal */
  .vmm-mi { width:100%; padding:9px 12px 9px 34px; height:40px; background:var(--nd-w05); border:1px solid var(--nd-w10); border-radius:9px; color:var(--nd-text); font-size:13px; font-family:'DM Sans',sans-serif; outline:none; transition:border-color 0.15s,background 0.15s; }
  .vmm-mi::placeholder { color:var(--nd-tx22); }
  .vmm-mi:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .vmm-mi:disabled { opacity:0.4; cursor:not-allowed; }
  .vmm-ml { display:block; font-size:10px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--nd-tx40); margin-bottom:5px; }
  .vmm-btn-submit { display:inline-flex; align-items:center; gap:7px; padding:9px 20px; border-radius:9px; border:none; color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; transition:opacity 0.15s; }
  .vmm-btn-submit:hover:not(:disabled) { opacity:.88; }
  .vmm-btn-submit:disabled { opacity:.45; cursor:not-allowed; }
  .vmm-btn-cancel { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; border-radius:9px; border:1px solid var(--nd-w10); background:var(--nd-w05); color:var(--nd-tx45); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .vmm-btn-cancel:hover { background:var(--nd-w08); color:var(--nd-tx70); }

  ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--nd-w10);border-radius:4px}
`;

const MField = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 15 }}>
    <label className="vmm-ml">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

// ── Dark overlay modal ────────────────────────────────────────────
const DarkModal = ({ show, onHide, title, iconColor, Icon, accentBg, children, footer }) => {
  if (!show) return null;
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',backdropFilter:'blur(4px)',zIndex:1050,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={onHide}>
      <div style={{ background:T.surface,border:`1px solid ${T.bord2}`,borderRadius:16,width:'100%',maxWidth:420,boxShadow:'0 24px 60px rgba(0,0,0,0.7)',animation:'modalIn 0.2s ease',fontFamily:T.font }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'15px 20px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <div style={{ display:'flex',alignItems:'center',gap:9 }}>
            <div style={{ width:28,height:28,borderRadius:8,background:accentBg,border:`1px solid ${iconColor}28`,display:'flex',alignItems:'center',justifyContent:'center' }}>
              <Icon size={14} color={iconColor} />
            </div>
            <span style={{ fontSize:14,fontWeight:700,color:T.text }}>{title}</span>
          </div>
          <button onClick={onHide} style={{ background:'var(--nd-w06)',border:'1px solid var(--nd-w10)',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:T.muted }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding:'18px 20px 4px' }}>{children}</div>
        <div style={{ padding:'12px 20px 16px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:8 }}>{footer}</div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
function MachineryVehiclesManagement() {
  const [vehicles, setVehicles]                   = useState([]);
  const [machinery, setMachinery]                 = useState([]);
  const [filteredVehicles, setFilteredVehicles]   = useState([]);
  const [filteredMachinery, setFilteredMachinery] = useState([]);
  const [vehicleForm, setVehicleForm]             = useState({ registration_number:'', miles_used:'' });
  const [machineryForm, setMachineryForm]         = useState({ name:'', kw_cleaned:'' });
  const [loading, setLoading]                     = useState(false);
  const [showVehicleModal, setShowVehicleModal]   = useState(false);
  const [showMachineryModal, setShowMachineryModal] = useState(false);
  const [vehicleSearch, setVehicleSearch]         = useState('');
  const [machinerySearch, setMachinerySearch]     = useState('');
  const [editingVehicle, setEditingVehicle]       = useState(null);
  const [editingMachinery, setEditingMachinery]   = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vRes, mRes] = await Promise.all([axios.get('/api/vehicles'), axios.get('/api/machinery')]);
      setVehicles(vRes.data); setMachinery(mRes.data);
      setFilteredVehicles(vRes.data); setFilteredMachinery(mRes.data);
    } catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    setFilteredVehicles(vehicleSearch
      ? vehicles.filter(v=>v.registration_number?.toLowerCase().includes(vehicleSearch.toLowerCase()))
      : vehicles);
  }, [vehicleSearch, vehicles]);

  useEffect(() => {
    setFilteredMachinery(machinerySearch
      ? machinery.filter(m=>m.name?.toLowerCase().includes(machinerySearch.toLowerCase()))
      : machinery);
  }, [machinerySearch, machinery]);

  const openVehicleModal = (v = null) => {
    setEditingVehicle(v);
    setVehicleForm(v ? { registration_number:v.registration_number, miles_used:v.miles_used } : { registration_number:'', miles_used:'' });
    setShowVehicleModal(true);
  };
  const openMachineryModal = (m = null) => {
    setEditingMachinery(m);
    setMachineryForm(m ? { name:m.name, kw_cleaned:m.kw_cleaned } : { name:'', kw_cleaned:'' });
    setShowMachineryModal(true);
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      editingVehicle ? await axios.put(`/api/vehicles/${editingVehicle.id}`, vehicleForm) : await axios.post('/api/vehicles/add', vehicleForm);
      setVehicleForm({ registration_number:'', miles_used:'' }); setShowVehicleModal(false); fetchData();
    } catch (err) { console.error('Failed to save vehicle:', err); }
    finally { setLoading(false); }
  };
  const handleMachinerySubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      editingMachinery ? await axios.put(`/api/machinery/${editingMachinery.id}`, machineryForm) : await axios.post('/api/machinery', machineryForm);
      setMachineryForm({ name:'', kw_cleaned:'' }); setShowMachineryModal(false); fetchData();
    } catch (err) { console.error('Failed to save machinery:', err); }
    finally { setLoading(false); }
  };
  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    setLoading(true);
    try { await axios.delete(`/api/vehicles/${id}`); fetchData(); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };
  const handleDeleteMachinery = async (id) => {
    if (!window.confirm('Delete this machinery?')) return;
    setLoading(true);
    try { await axios.delete(`/api/machinery/${id}`); fetchData(); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const SectionCard = ({ title, icon: Icon, iconColor, accentBg, count, countColor, children, onAdd, addLabel }) => (
    <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',animation:'fadeUp 0.3s ease both' }}>
      <div style={{ padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
        <div style={{ display:'flex',alignItems:'center',gap:9 }}>
          <div style={{ width:26,height:26,borderRadius:7,background:accentBg,border:`1px solid ${iconColor}28`,display:'flex',alignItems:'center',justifyContent:'center' }}>
            <Icon size={13} color={iconColor} />
          </div>
          <span style={{ fontSize:13,fontWeight:700,color:T.text }}>{title}</span>
          <span style={{ padding:'2px 9px',borderRadius:5,fontSize:10,fontWeight:700,fontFamily:T.mono,background:`${countColor}12`,color:countColor,border:`1px solid ${countColor}25` }}>{count} Total</span>
        </div>
        <button className="vmm-btn-add" onClick={onAdd}><Plus size={12} /> {addLabel}</button>
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ background:T.bg, fontFamily:T.font, color:T.text, padding:'14px 20px' }}>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,marginBottom:18,overflow:'hidden' }}>
        <div style={{ padding:'18px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:14 }}>
          <div>
            <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.18em',marginBottom:5 }}>iNOVAA · OPERATIONS</div>
            <div style={{ fontSize:26,fontWeight:800,color:'var(--nd-text-strong)',letterSpacing:'-0.03em',lineHeight:1 }}>Fleet &amp; Equipment Management</div>
            <div style={{ fontSize:12,color:T.muted,marginTop:5 }}>Manage vehicles and machinery assets</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.14em',textTransform:'uppercase',marginBottom:4 }}>Total Assets</div>
            <div style={{ fontSize:36,fontWeight:900,color:T.teal,fontFamily:T.mono,lineHeight:1 }}>{vehicles.length+machinery.length}</div>
          </div>
        </div>
        <div style={{ height:2,background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {loading && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',backdropFilter:'blur(2px)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',gap:12 }}>
          <Loader size={22} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
          <span style={{ color:T.text,fontFamily:T.font,fontSize:13 }}>Saving…</span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))', gap:16 }}>

        {/* ── Vehicles ── */}
        <SectionCard title="Vehicles" icon={Truck} iconColor={T.blue} accentBg="rgba(59,130,246,0.12)" count={vehicles.length} countColor={T.blue} onAdd={()=>openVehicleModal()} addLabel="Add Vehicle">
          <div style={{ padding:'12px 14px 10px' }}>
            <div style={{ position:'relative' }}>
              <Search size={11} color={T.muted} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} />
              <input className="vmm-input" type="text" placeholder="Search by registration…" value={vehicleSearch} onChange={e=>setVehicleSearch(e.target.value)} />
            </div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead><tr>
                <th className="vmm-th">Registration</th>
                <th className="vmm-th" style={{ textAlign:'center' }}>Miles</th>
                <th className="vmm-th">Date Added</th>
                <th className="vmm-th" style={{ textAlign:'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {filteredVehicles.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding:'36px',textAlign:'center',color:T.muted,fontSize:12 }}>
                    <div style={{ fontSize:32,marginBottom:8 }}>🚗</div>No vehicles found
                  </td></tr>
                ) : filteredVehicles.map(v=>(
                  <tr key={v.id} className="vmm-tr">
                    <td className="vmm-td" style={{ fontWeight:700,color:T.text,fontSize:12 }}>{v.registration_number}</td>
                    <td className="vmm-td" style={{ textAlign:'center' }}>
                      <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'2px 9px',borderRadius:5,fontSize:11,fontWeight:700,fontFamily:T.mono,background:'rgba(34,211,238,0.10)',color:T.cyan,border:'1px solid rgba(34,211,238,0.22)' }}>
                        <Gauge size={10} />{v.miles_used}
                      </span>
                    </td>
                    <td className="vmm-td"><div style={{ display:'flex',alignItems:'center',gap:5,fontSize:11,color:T.muted,fontFamily:T.mono }}><Calendar size={10} />{new Date(v.created_at).toLocaleDateString('en-GB')}</div></td>
                    <td className="vmm-td" style={{ textAlign:'center' }}>
                      <div style={{ display:'flex',gap:6,justifyContent:'center' }}>
                        <button className="vmm-act vmm-act-edit"  onClick={()=>openVehicleModal(v)}><Edit2  size={11} /></button>
                        <button className="vmm-act vmm-act-delete" onClick={()=>handleDeleteVehicle(v.id)}><Trash2 size={11} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* ── Machinery ── */}
        <SectionCard title="Machinery" icon={Settings} iconColor={T.teal} accentBg="rgba(34,211,165,0.12)" count={machinery.length} countColor={T.teal} onAdd={()=>openMachineryModal()} addLabel="Add Machine">
          <div style={{ padding:'12px 14px 10px' }}>
            <div style={{ position:'relative' }}>
              <Search size={11} color={T.muted} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} />
              <input className="vmm-input" type="text" placeholder="Search by name…" value={machinerySearch} onChange={e=>setMachinerySearch(e.target.value)} />
            </div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead><tr>
                <th className="vmm-th">Name</th>
                <th className="vmm-th" style={{ textAlign:'center' }}>kW Cleaned</th>
                <th className="vmm-th">Date Added</th>
                <th className="vmm-th" style={{ textAlign:'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {filteredMachinery.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding:'36px',textAlign:'center',color:T.muted,fontSize:12 }}>
                    <div style={{ fontSize:32,marginBottom:8 }}>⚙️</div>No machinery found
                  </td></tr>
                ) : filteredMachinery.map(m=>(
                  <tr key={m.id} className="vmm-tr">
                    <td className="vmm-td" style={{ fontWeight:700,color:T.text,fontSize:12 }}>{m.name}</td>
                    <td className="vmm-td" style={{ textAlign:'center' }}>
                      <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'2px 9px',borderRadius:5,fontSize:11,fontWeight:700,fontFamily:T.mono,background:'rgba(251,191,36,0.10)',color:T.amber,border:'1px solid rgba(251,191,36,0.22)' }}>
                        <Zap size={10} />{m.kw_cleaned} kW
                      </span>
                    </td>
                    <td className="vmm-td"><div style={{ display:'flex',alignItems:'center',gap:5,fontSize:11,color:T.muted,fontFamily:T.mono }}><Calendar size={10} />{new Date(m.created_at).toLocaleDateString('en-GB')}</div></td>
                    <td className="vmm-td" style={{ textAlign:'center' }}>
                      <div style={{ display:'flex',gap:6,justifyContent:'center' }}>
                        <button className="vmm-act vmm-act-edit"  onClick={()=>openMachineryModal(m)}><Edit2  size={11} /></button>
                        <button className="vmm-act vmm-act-delete" onClick={()=>handleDeleteMachinery(m.id)}><Trash2 size={11} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Vehicle modal */}
      <DarkModal
        show={showVehicleModal} onHide={()=>setShowVehicleModal(false)}
        title={editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
        Icon={editingVehicle ? Edit2 : Plus} iconColor={T.blue} accentBg="rgba(59,130,246,0.12)"
        footer={<>
          <button className="vmm-btn-cancel" onClick={()=>setShowVehicleModal(false)}>Cancel</button>
          <button className="vmm-btn-submit" style={{ background:'linear-gradient(135deg,#3B82F6,#22D3A5)',boxShadow:'0 4px 14px rgba(59,130,246,0.25)' }} onClick={handleVehicleSubmit} disabled={loading}>
            {loading ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Saving…</> : <><CheckCircle size={13} /> {editingVehicle ? 'Save Changes' : 'Add Vehicle'}</>}
          </button>
        </>}
      >
        <MField label="Registration Number" icon={Truck} iconColor={T.blue}>
          <input className="vmm-mi" type="text" required placeholder="Enter registration number" value={vehicleForm.registration_number} onChange={e=>setVehicleForm({...vehicleForm,registration_number:e.target.value})} />
        </MField>
        <MField label="Miles Used" icon={Gauge} iconColor={T.cyan}>
          <input className="vmm-mi" type="number" required placeholder="Enter miles used" value={vehicleForm.miles_used} onChange={e=>setVehicleForm({...vehicleForm,miles_used:e.target.value})} />
        </MField>
      </DarkModal>

      {/* Machinery modal */}
      <DarkModal
        show={showMachineryModal} onHide={()=>setShowMachineryModal(false)}
        title={editingMachinery ? 'Edit Machinery' : 'Add Machinery'}
        Icon={editingMachinery ? Edit2 : Plus} iconColor={T.teal} accentBg="rgba(34,211,165,0.12)"
        footer={<>
          <button className="vmm-btn-cancel" onClick={()=>setShowMachineryModal(false)}>Cancel</button>
          <button className="vmm-btn-submit" style={{ background:'linear-gradient(135deg,#22D3A5,#3B82F6)',boxShadow:'0 4px 14px rgba(34,211,165,0.22)' }} onClick={handleMachinerySubmit} disabled={loading}>
            {loading ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Saving…</> : <><CheckCircle size={13} /> {editingMachinery ? 'Save Changes' : 'Add Machinery'}</>}
          </button>
        </>}
      >
        <MField label="Machine Name" icon={Settings} iconColor={T.teal}>
          <input className="vmm-mi" type="text" required placeholder="Enter machine name" value={machineryForm.name} onChange={e=>setMachineryForm({...machineryForm,name:e.target.value})} />
        </MField>
        <MField label="kW Cleaned" icon={Zap} iconColor={T.amber}>
          <input className="vmm-mi" type="number" required placeholder="Enter kW cleaned" value={machineryForm.kw_cleaned} onChange={e=>setMachineryForm({...machineryForm,kw_cleaned:e.target.value})} />
        </MField>
      </DarkModal>
    </div>
  );
}

export default MachineryVehiclesManagement; 